import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AlertTriangle, CheckCircle, Clock, FileText, RefreshCw } from "lucide-react";
import complianceApi from "../../api/complianceApi";

const getCurrentFiscalYear = () => {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `FY${year}-${(year + 1).toString().slice(-2)}`;
};

const BeforeMarchDashboard = () => {
  const token = useSelector((state) => state.auth.access_token);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [fiscalYear, setFiscalYear] = useState(getCurrentFiscalYear());

  const fiscalYears = ["FY2025-26", "FY2024-25", "FY2023-24"];

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await complianceApi.getBeforeMarchDashboard(fiscalYear, token);
      setData(response);
    } catch (error) {
      console.error("Error fetching Before March dashboard:", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, fiscalYear]);

  const getCompletionPercentage = (completed, total) => {
    if (!total || total === 0) return 0;
    return Math.round((completed / total) * 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return "bg-emerald-500";
    if (percentage >= 50) return "bg-yellow-500";
    return "bg-red-500";
  };

  // Calculate overall stats
  const overallStats = data?.accounts?.reduce(
    (acc, account) => ({
      totalRequired: acc.totalRequired + (account.totalRequired || 0),
      completed: acc.completed + (account.completed || 0),
      pending: acc.pending + (account.pending || 0),
    }),
    { totalRequired: 0, completed: 0, pending: 0 }
  ) || { totalRequired: 0, completed: 0, pending: 0 };

  const overallPercentage = getCompletionPercentage(overallStats.completed, overallStats.totalRequired);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">
                Before March - Year End Documents
              </h3>
              <p className="text-sm text-slate-500">
                Critical documents required before March closing
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={fiscalYear}
              onChange={(e) => setFiscalYear(e.target.value)}
              className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {fiscalYears.map((fy) => (
                <option key={fy} value={fy}>
                  {fy}
                </option>
              ))}
            </select>

            <button
              onClick={fetchData}
              disabled={loading}
              className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Overall Progress */}
        <div className="mt-4 p-4 bg-slate-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">
              Overall Completion
            </span>
            <span className="text-sm font-bold text-slate-800">
              {overallPercentage}%
            </span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all duration-500 ${getProgressColor(overallPercentage)}`}
              style={{ width: `${overallPercentage}%` }}
            ></div>
          </div>
          <div className="flex justify-between mt-2 text-xs text-slate-500">
            <span>
              <CheckCircle className="inline w-3 h-3 mr-1 text-emerald-500" />
              {overallStats.completed} Completed
            </span>
            <span>
              <Clock className="inline w-3 h-3 mr-1 text-orange-500" />
              {overallStats.pending} Pending
            </span>
            <span>
              <FileText className="inline w-3 h-3 mr-1 text-slate-400" />
              {overallStats.totalRequired} Total Required
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : !data || !data.accounts || data.accounts.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            No year-end document data available
          </div>
        ) : (
          <div className="space-y-4">
            {data.accounts.map((account) => {
              const percentage = getCompletionPercentage(account.completed, account.totalRequired);
              return (
                <div
                  key={account.accountId}
                  className="border border-slate-200 rounded-lg overflow-hidden"
                >
                  {/* Account Header */}
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                            percentage >= 80
                              ? "bg-emerald-500"
                              : percentage >= 50
                              ? "bg-yellow-500"
                              : "bg-red-500"
                          }`}
                        >
                          {percentage}%
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">
                            {account.accountName}
                          </div>
                          <div className="text-xs text-slate-500">
                            {account.completed}/{account.totalRequired} documents completed
                          </div>
                        </div>
                      </div>

                      {account.pending > 0 && (
                        <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                          {account.pending} Pending
                        </span>
                      )}
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-3 w-full bg-slate-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${getProgressColor(percentage)}`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Missing Documents */}
                  {account.missingDocuments && account.missingDocuments.length > 0 && (
                    <div className="px-4 py-3">
                      <div className="text-xs font-medium text-slate-600 mb-2">
                        Missing/Pending Documents:
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {account.missingDocuments.map((doc, idx) => (
                          <div
                            key={idx}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 ${
                              doc.isUploaded
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {doc.isUploaded ? (
                              <Clock className="w-3 h-3" />
                            ) : (
                              <AlertTriangle className="w-3 h-3" />
                            )}
                            {doc.documentType}
                            <span className="text-xs opacity-75">({doc.category})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Year-End Critical Documents Reference */}
      <div className="px-4 py-3 border-t border-slate-200 bg-slate-50">
        <div className="text-xs text-slate-600">
          <span className="font-medium">Year-End Critical Documents:</span>
          <span className="ml-2">
            Depreciation Schedule • Prepaid Expense Reversal • GSTR-9 Annual Return • Tax Audit Report
          </span>
        </div>
      </div>
    </div>
  );
};

export default BeforeMarchDashboard;
